import type { Selectable } from "@lobomfz/db";

import type { DocumentKind, Schema } from "@/api/db/connection";
import type { DocumentBlock, InlineNode } from "@/api/document/block";
import { DocumentFields, type FieldContext } from "@/api/document/fields";
import { A4_METRICS } from "@/api/document/paginate";
import { documentStyles } from "@/api/document/styles";

type FieldRow = Selectable<Schema["document_field"]>;

const HTML_ENTITIES: Record<string, string> = {
	"&": "&amp;",
	"<": "&lt;",
	">": "&gt;",
	'"': "&quot;",
	"'": "&#39;",
};

function esc(value: string) {
	return value.replaceAll(/[&<>"']/gu, (char) => HTML_ENTITIES[char]);
}

type SequenceBlock = Exclude<DocumentBlock, { type: "page-break" }>;

type FieldMode = "preview" | "resolved" | "template";

class Walker {
	private steps = 0;

	constructor(
		private fields: Map<number, FieldRow>,
		private context: FieldContext,
		private mode: FieldMode = "resolved",
		private flatList = false,
		private imageSrc?: (file: string) => string,
	) {}

	sequence(blocks: DocumentBlock[]) {
		const parts: string[] = [];
		let items: string[] = [];

		const flush = () => {
			if (items.length > 0) {
				parts.push(this.flatList ? items.join("") : `<ul>${items.join("")}</ul>`);
				items = [];
			}
		};

		for (const block of blocks) {
			if (block.type === "page-break") {
				flush();
				parts.push(`<div class="page-break"></div>`);

				continue;
			}

			if (block.type === "list-item") {
				items.push(
					`<li data-block-id="${esc(block.attrs.id)}">${this.inlines(block.content)}</li>`,
				);

				continue;
			}

			flush();
			parts.push(this.block(block));
		}

		flush();

		return parts.join("\n");
	}

	private block(block: Exclude<SequenceBlock, { type: "list-item" }>) {
		const idAttr = ` data-block-id="${esc(block.attrs.id)}"`;

		switch (block.type) {
			case "heading": {
				const cls = block.attrs.align === "center" ? ` class="center"` : "";

				return `<h${block.attrs.level}${cls}${idAttr}>${this.inlines(block.content)}</h${block.attrs.level}>`;
			}
			case "paragraph": {
				const classes: string[] = [];

				if (block.attrs.emphasis !== undefined) {
					classes.push(block.attrs.emphasis);
				}

				if (block.attrs.align === "center") {
					classes.push("center");
				}

				const cls = classes.length === 0 ? "" : ` class="${classes.join(" ")}"`;

				return `<p${cls}${idAttr}>${this.inlines(block.content)}</p>`;
			}
			case "step": {
				this.steps += 1;

				return `<div class="step"${idAttr}><p class="step-title">${this.steps}. ${esc(block.attrs.title)}</p><p>${this.inlines(block.content)}</p></div>`;
			}
			case "table": {
				const body = block.content
					.map(
						(row) =>
							`<tr>${row.content.map((cell) => `<td>${this.inlines(cell.content)}</td>`).join("")}</tr>`,
					)
					.join("");

				return `<table${idAttr}><tbody>${body}</tbody></table>`;
			}
			case "variant-card":
				return `<div class="card card-${block.attrs.variant}"${idAttr}>${this.sequence(block.content)}</div>`;
			case "image": {
				const { file, width, height, align } = block.attrs;
				const cls = align === undefined ? "doc-image" : `doc-image ${align}`;

				return `<figure class="${cls}"${idAttr}><img src="${esc(this.resolveImage(file))}" style="width:${width}px;height:${height}px" alt=""></figure>`;
			}
			case "signature": {
				const rows = block.content
					.map((row) => {
						const slots = row.content
							.map(
								(slot) =>
									`<div class="signature-slot"><div class="signature-line"></div><p class="signature-label">${this.inlines(slot.content)}</p></div>`,
							)
							.join("");

						return `<div class="signature-row">${slots}</div>`;
					})
					.join("");

				return `<div class="signature"${idAttr}>${rows}</div>`;
			}
			case "pattern": {
				if (this.mode === "resolved") {
					throw new Error(`unmaterialized_document_pattern:${block.attrs.patternId}`);
				}

				return `<div class="doc-pattern"${idAttr}>${this.sequence(block.content)}</div>`;
			}
		}
	}

	private resolveImage(file: string) {
		if (this.imageSrc === undefined) {
			throw new Error(`unresolved_document_image:${file}`);
		}

		return this.imageSrc(file);
	}

	private inlines(content: InlineNode[] | undefined) {
		if (content === undefined) {
			return "";
		}

		return content.map((node) => this.inline(node)).join("");
	}

	private inline(node: InlineNode) {
		if (node.type === "field") {
			return this.fieldInline(node.attrs.fieldId);
		}

		const text = esc(node.text);
		const marks = node.marks ?? [];

		if (marks.length === 0) {
			return text;
		}

		const has = (type: "bold" | "underline" | "italic") => marks.some((mark) => mark.type === type);

		let out = text;

		if (has("italic")) {
			out = `<em>${out}</em>`;
		}

		if (has("underline")) {
			out = `<u>${out}</u>`;
		}

		if (has("bold")) {
			out = `<strong>${out}</strong>`;
		}

		return out;
	}

	private fieldInline(fieldId: number) {
		const field = this.fields.get(fieldId);

		if (field === undefined) {
			throw new Error(`unknown_document_field:${fieldId}`);
		}

		if (this.mode === "template") {
			return `<span class="field-chip">${esc(field.label)}</span>`;
		}

		const value = DocumentFields.resolve(
			{
				id: fieldId,
				column: field.column,
				format: field.format,
				input_type: field.input_type,
			},
			this.context,
		);

		if (this.mode === "resolved") {
			if (value === null) {
				throw new Error(`unresolved_document_field:${fieldId}`);
			}

			return esc(value);
		}

		const shown = value ?? field.label;
		const chip = value === null ? ` class="field-chip"` : "";
		const dataValue = value === null ? "" : ` data-value="${esc(value)}"`;

		return `<span${chip} data-component="document-field" data-field-id="${fieldId}"${dataValue}>${esc(shown)}</span>`;
	}
}

function splitPages(blocks: DocumentBlock[]) {
	const pages: SequenceBlock[][] = [];
	let current: SequenceBlock[] = [];

	for (const block of blocks) {
		if (block.type === "page-break") {
			if (current.length > 0) {
				pages.push(current);
				current = [];
			}

			continue;
		}

		current.push(block);
	}

	if (current.length > 0) {
		pages.push(current);
	}

	return pages;
}

interface PagesInput {
	blocks: DocumentBlock[];
	fields: Map<number, FieldRow>;
	context: FieldContext;
}

interface RenderInput extends PagesInput {
	kind: DocumentKind;
	title: string;
}

export interface TermMargins {
	top: number;
	right: number;
	bottom: number;
	left: number;
}

export interface TermTypography {
	fontSize: number;
	lineHeight: number;
	fontFamily: "serif" | "sans";
}

export const TERM_TYPE_DEFAULTS: TermTypography = {
	fontSize: 14.67,
	lineHeight: 1.5,
	fontFamily: "serif",
};

export const TERM_FONT_STACKS: Record<"serif" | "sans", string> = {
	serif: `"Times New Roman",Georgia,serif`,
	sans: `Arial,"Helvetica Neue",sans-serif`,
};

interface TermFlowInput extends PagesInput {
	mode: FieldMode;
	imageSrc?: (file: string) => string;
}

interface TermRenderInput extends TermFlowInput {
	title: string;
	margins?: TermMargins;
	typography?: TermTypography;
}

function termStyleVars(margins: TermMargins, typography?: TermTypography) {
	const { top, right, bottom, left } = margins;

	const parts = [
		`--t-pad-top:${top}px`,
		`--t-pad-right:${right}px`,
		`--t-pad-bottom:${bottom}px`,
		`--t-pad-left:${left}px`,
		`--t-flow-width:${A4_METRICS.pageWidth - left - right}px`,
	];

	if (typography !== undefined) {
		parts.push(
			`--t-font-size:${typography.fontSize}px`,
			`--t-leading:${typography.lineHeight}`,
			`--t-font:${TERM_FONT_STACKS[typography.fontFamily]}`,
		);
	}

	return `:root{${parts.join(";")}}`;
}

function pages(input: PagesInput) {
	const walker = new Walker(input.fields, input.context);

	return splitPages(input.blocks).map((page) => walker.sequence(page));
}

function shell(input: {
	kind: DocumentKind;
	title: string;
	body: string;
	head?: string;
	styleVars?: string;
}) {
	return `<!doctype html>
<html lang="pt-BR">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
${input.head === undefined ? "" : `${input.head}\n`}<title>${esc(input.title)}</title>
<style>body{margin:0}</style>
${input.styleVars === undefined ? "" : `<style>${input.styleVars}</style>\n`}<style>${documentStyles[input.kind]}</style>
</head>
<body>
<div class="doc-${input.kind}">
${input.body}
</div>
</body>
</html>`;
}

function frame(input: {
	kind: DocumentKind;
	title: string;
	sections: string[];
	head?: string;
	styleVars?: string;
}) {
	const sections = input.sections
		.map((section) => `<section class="doc-page">${section}</section>`)
		.join("\n");

	return shell({
		kind: input.kind,
		title: input.title,
		head: input.head,
		styleVars: input.styleVars,
		body: sections,
	});
}

export const DocumentRender = {
	esc,
	pages,
	frame,
	termFlow(input: TermFlowInput) {
		const walker = new Walker(input.fields, input.context, input.mode, true, input.imageSrc);

		return walker.sequence(input.blocks);
	},

	html(input: RenderInput) {
		return frame({ kind: input.kind, title: input.title, sections: pages(input) });
	},

	termHtml(input: TermRenderInput) {
		const body = DocumentRender.termFlow(input);

		return shell({
			kind: "term",
			title: input.title,
			body: `<section class="doc-flow">\n${body}\n</section>`,
			styleVars:
				input.margins === undefined ? undefined : termStyleVars(input.margins, input.typography),
		});
	},
};
